import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Coach {
  id: string;
  email: string;
}

interface CreateSessionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  coaches: Coach[];
  onCreate: (data: {
    date: string;
    startTime: string;
    endTime: string;
    sessionType?: string;
    courtNumber?: number;
    coachUserId?: string;
    repeat: string;
    repeatUntil: string;
  }) => Promise<void>;
  isPending: boolean;
  initialData?: {
    sessionType?: string;
    startTime?: string;
    endTime?: string;
  };
}

export function CreateSessionForm({
  open,
  onOpenChange,
  coaches,
  onCreate,
  isPending,
  initialData,
}: CreateSessionFormProps) {
  const [formData, setFormData] = useState({
    date: '',
    startTime: initialData?.startTime ?? '',
    endTime: initialData?.endTime ?? '',
    sessionType: initialData?.sessionType ?? '',
    courtNumber: '',
    coachUserId: '',
    repeat: 'none',
    repeatUntil: '',
  });

  // Sync initialData when modal opens with template data
  const prevInitial = useState(initialData)[0];
  if (initialData !== prevInitial && initialData) {
    setFormData((prev) => ({
      ...prev,
      sessionType: initialData.sessionType ?? prev.sessionType,
      startTime: initialData.startTime ?? prev.startTime,
      endTime: initialData.endTime ?? prev.endTime,
    }));
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await onCreate({
      date: formData.date,
      startTime: formData.startTime,
      endTime: formData.endTime,
      sessionType: formData.sessionType || undefined,
      courtNumber: formData.courtNumber ? parseInt(formData.courtNumber) : undefined,
      coachUserId: formData.coachUserId || undefined,
      repeat: formData.repeat,
      repeatUntil: formData.repeatUntil,
    });
    setFormData({ date: '', startTime: '', endTime: '', sessionType: '', courtNumber: '', coachUserId: '', repeat: 'none', repeatUntil: '' });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Schedule New Session</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleCreate} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="date">Date *</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime">Start Time *</Label>
              <Input
                id="startTime"
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime">End Time *</Label>
              <Input
                id="endTime"
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sessionType">Session Type</Label>
            <Select
              value={formData.sessionType}
              onValueChange={(value) => setFormData({ ...formData, sessionType: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="group">Group</SelectItem>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="clinic">Clinic</SelectItem>
                <SelectItem value="match_play">Match Play</SelectItem>
                <SelectItem value="cardio_tennis">Cardio Tennis</SelectItem>
                <SelectItem value="squads">Squads</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="courtNumber">Court Number</Label>
            <Input
              id="courtNumber"
              type="number"
              min="1"
              placeholder="e.g. 1"
              value={formData.courtNumber}
              onChange={(e) => setFormData({ ...formData, courtNumber: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="coach">Assign Coach</Label>
            <Select
              value={formData.coachUserId}
              onValueChange={(value) => setFormData({ ...formData, coachUserId: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select coach (optional)" />
              </SelectTrigger>
              <SelectContent>
                {coaches.map((coach) => (
                  <SelectItem key={coach.id} value={coach.id}>
                    {coach.email.split('@')[0]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="repeat">Repeat</Label>
              <Select
                value={formData.repeat}
                onValueChange={(value) => setFormData({ ...formData, repeat: value, repeatUntil: '' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No repeat</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.repeat !== 'none' && (
              <div className="space-y-2">
                <Label htmlFor="repeatUntil">Repeat Until *</Label>
                <Input
                  id="repeatUntil"
                  type="date"
                  min={formData.date}
                  value={formData.repeatUntil}
                  onChange={(e) => setFormData({ ...formData, repeatUntil: e.target.value })}
                  required
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
            >
              {isPending ? 'Creating...' : 'Create Session'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
